import { Outlet, useLocation } from 'react-router-dom';
import { Suspense, createContext, useCallback, useContext, useState, lazy } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useGetUsersMe } from '@/src/api/users';
import { useGetTags } from '@/src/api/tags';
import { InstructionsButton } from '@/src/components/ui/instructions/instructions-button';
import { Sidebar } from '@/src/components/ui/layout/sidebar';
import { cn } from '@/src/lib/shadcn/cn';
import { useClicksterStore } from '@/src/stores';
import { setUserStore } from '@/src/stores/user/actions';
import { TagsManagerActions } from '@/src/stores/tags/actions';
import { getAuthToken } from '@/src/lib/axios/auth-token';

// Lazy load modals to avoid hydration issues (though we are SPA now) and reduce bundle size
const AuthListener = lazy(() => import('@/src/components/auth-modal/auth-listener'));
const AuthModalWrapper = lazy(() => import('@/src/components/auth-modal/wrapper'));
const VersionModalManager = lazy(() =>
  import('@/src/components/modals/version-modal').then((mod) => ({ default: mod.VersionModalManager }))
);
const ProfileModalWrapper = lazy(() => import('@/src/components/ui/modals/profile-modal-wrapper'));

const SIDEBAR_STORAGE_KEY = 'clickster-sidebar-visible';

function getInitialSidebarState(): boolean {
  try {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (stored !== null) return stored === 'true';
  } catch { /* ignore */ }
  return false;
}

// Sidebar context
export const SidebarContext = createContext<{
  showSidebar: boolean;
  setShowSidebar: (show: boolean) => void;
}>({
  showSidebar: false,
  setShowSidebar: () => {},
});

export const useSidebar = () => useContext(SidebarContext);

export function ProtectedLayout() {
  const { userInSession } = useClicksterStore((state) => state);
  const location = useLocation();
  const isCKFinder = location.pathname.includes('iframes/ckfinder');
  const [showSidebar, setShowSidebarState] = useState(getInitialSidebarState);

  const setShowSidebar = useCallback((show: boolean) => {
    setShowSidebarState(show);
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(show));
    } catch { /* ignore */ }
  }, []);
  const token = getAuthToken();

  useGetUsersMe({
    enabled: !!(!userInSession?._id && token),
    staleTime: 60 * 60 * 1000,
    handleSuccess(data) {
      setUserStore(data);
    },
    handleError(error) {
      console.error('Error fetching user:', error);
    },
  });

  useGetTags({
    enabled: !!userInSession?._id,
    staleTime: 60 * 60 * 1000,
    handleSuccess(data) {
      TagsManagerActions.tags.setAvailableTags(data);
    },
    params: {
      sortField: 'name',
      sortOrder: 'asc',
      searchAttribute: 'status',
      search: 'active',
    },
  });

  return (
    <>
      {isCKFinder ? (
        <Outlet />
      ) : (
        <Suspense fallback="">
          <SidebarContext.Provider value={{ showSidebar, setShowSidebar }}>
            <DndProvider backend={HTML5Backend}>
              <div className="flex min-h-screen">
                {showSidebar && <Sidebar />}
                <div
                  className={cn(
                    'flex-1 min-w-0 overflow-x-clip',
                    showSidebar ? 'ml-[84px]' : '',
                    'transition-[margin] duration-300 ease-in-out relative'
                  )}
                  id="main-content"
                >
                  <InstructionsButton />
                  <Outlet />
                </div>
              </div>
            </DndProvider>
          </SidebarContext.Provider>
        </Suspense>
      )}
      <Suspense fallback={null}>
        <AuthListener />
        <AuthModalWrapper />
        <VersionModalManager />
        <ProfileModalWrapper />
      </Suspense>
    </>
  );
}
